import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useToast } from '../../context/ToastContext';
import Icon from '../common/Icon';
import './CommandPalette.css';

const COMMANDS = [
  // Navigation
  { section: 'Navigation', icon: '📊', label: 'Dashboard', shortcut: 'G D', path: '/' },
  { section: 'Navigation', icon: '🏢', label: 'Tenants', shortcut: 'G T', path: '/tenants' },
  { section: 'Navigation', icon: '👥', label: 'Users', shortcut: 'G U', path: '/users' },
  { section: 'Navigation', icon: '💰', label: 'Revenue', shortcut: 'G R', path: '/revenue' },
  { section: 'Navigation', icon: '📋', label: 'Plans', shortcut: 'G P', path: '/plans' },
  { section: 'Navigation', icon: '🛡️', label: 'Fraud Dashboard', path: '/fraud' },
  { section: 'Navigation', icon: '📜', label: 'Activity Log', path: '/activity' },
  { section: 'Navigation', icon: '👨‍💻', label: 'Developers', path: '/developers' },
  { section: 'Navigation', icon: '⚙️', label: 'Settings', path: '/settings' },
  { section: 'Navigation', icon: '🤖', label: 'AI Load Balancer', path: '/ai-loadbalancer' },
  { section: 'Navigation', icon: '🎨', label: 'Whitelabel Wizard', path: '/whitelabel' },

  // Actions
  { section: 'Actions', icon: '➕', label: 'Create New Tenant', action: 'createTenant' },
  { section: 'Actions', icon: '🌓', label: 'Toggle Theme', action: 'toggleTheme' },
  { section: 'Actions', icon: '🔑', label: 'Generate API Key', action: 'generateApiKey' },

  // Lazy Marketing
  { section: 'Lazy Marketing', icon: '🎯', label: 'CEO Dashboard', path: '/marketing' },
  { section: 'Lazy Marketing', icon: '👤', label: 'Leads & CRM', path: '/marketing/leads' },
  { section: 'Lazy Marketing', icon: '✉️', label: 'Cold Email', path: '/marketing/email' },
  { section: 'Lazy Marketing', icon: '📢', label: 'Paid Ads', path: '/marketing/ads' },
  { section: 'Lazy Marketing', icon: '📱', label: 'SMS Marketing', path: '/marketing/sms' },
  { section: 'Lazy Marketing', icon: '📊', label: 'Analytics', path: '/marketing/analytics' },
];

const CommandPalette = ({ isOpen, onClose, onCreateTenant }) => {
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  const { showToast } = useToast();

  const filteredCommands = COMMANDS.filter(cmd =>
    cmd.label.toLowerCase().includes(query.toLowerCase())
  );

  // Group by section
  const groupedCommands = filteredCommands.reduce((acc, cmd) => {
    if (!acc[cmd.section]) acc[cmd.section] = [];
    acc[cmd.section].push(cmd);
    return acc;
  }, {});

  const flatCommands = filteredCommands;

  const executeCommand = useCallback((cmd) => {
    if (cmd.path) {
      navigate(cmd.path);
    } else if (cmd.action === 'toggleTheme') {
      toggleTheme();
    } else if (cmd.action === 'createTenant') {
      onCreateTenant?.();
    } else if (cmd.action === 'generateApiKey') {
      showToast('New API key generated', 'success');
    }
    onClose();
    setQuery('');
    setSelectedIndex(0);
  }, [navigate, toggleTheme, showToast, onClose, onCreateTenant]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(i => Math.min(i + 1, flatCommands.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter' && flatCommands[selectedIndex]) {
      e.preventDefault();
      executeCommand(flatCommands[selectedIndex]);
    }
  }, [flatCommands, selectedIndex, executeCommand]);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
      setQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query]);

  if (!isOpen) return null;

  return (
    <div className="cp-container">
      <div className="cp-overlay" onClick={onClose} />
      <div className="cp-modal">
        <div className="cp-search">
          <Icon name="search" size="sm" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Search commands, pages, settings..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <kbd>ESC</kbd>
        </div>
        <div className="cp-results">
          {Object.entries(groupedCommands).map(([section, commands]) => (
            <div key={section}>
              <div className="cp-section">{section}</div>
              {commands.map((cmd, idx) => {
                const globalIndex = flatCommands.indexOf(cmd);
                return (
                  <div
                    key={cmd.label}
                    className={`cp-item ${globalIndex === selectedIndex ? 'selected' : ''}`}
                    onClick={() => executeCommand(cmd)}
                  >
                    <span className="cp-icon">{cmd.icon}</span>
                    <span className="cp-label">{cmd.label}</span>
                    {cmd.shortcut && <kbd>{cmd.shortcut}</kbd>}
                  </div>
                );
              })}
            </div>
          ))}
          {flatCommands.length === 0 && (
            <div className="cp-empty">No commands found</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
